import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardActions, CardContent, Container, Grid, Typography, Chip } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState<any>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await api.get('/site-settings');
        setPricing(res.data?.data || {});
      } catch (err) {
        console.error('Failed to load access pricing', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
  }, []);

  const individualAccess = pricing?.individualAccess || {};
  const accessCategories = [
    { key: 'jamb', label: 'Explore JAMB', amount: Number(individualAccess.jamb || 0) },
    { key: 'waec', label: 'Explore WAEC', amount: Number(individualAccess.waec || 0) },
    { key: 'jambMockTest', label: 'JAMB Mock Test', amount: Number(individualAccess.jambMockTest || 0) },
    { key: 'waecMockTest', label: 'WAEC Mock Test', amount: Number(individualAccess.waecMockTest || 0) },
    { key: 'jambResultCheck', label: 'Check JAMB Results', amount: Number(individualAccess.jambResultCheck || 0) },
    { key: 'waecResultCheck', label: 'Check WAEC Results', amount: Number(individualAccess.waecResultCheck || 0) },
    { key: 'labs', label: 'Labs', amount: Number(individualAccess.labs || 0) },
    { key: 'languageHub', label: 'Language Hub', amount: Number(individualAccess.languageHub || 0) },
  ].filter(item => item.amount > 0);

  return (
    <Container maxWidth="lg" sx={{ mt: 8, mb: 8 }}>
      <Box textAlign="center" mb={6}>
        <Typography variant="h3" component="h1" gutterBottom>
          School Upgrade Required
        </Typography>
        <Typography variant="h5" color="textSecondary" paragraph>
          Access to premium content is granted through your school's active subscription.
        </Typography>
      </Box>

      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} md={8}>
          <Card elevation={3}>
            <CardContent>
              <Typography variant="h4" component="h2" gutterBottom color="primary">
                Ask Your School to Upgrade
              </Typography>
              <Typography variant="body1" color="textSecondary" paragraph>
                Students cannot purchase premium access directly. Only school administrators can activate subscriptions and grant access to enrolled users.
              </Typography>

              <Box component="ul" sx={{ listStyle: 'none', pl: 0, m: 0 }}>
                {[
                  'Your school admin must log in at the School Admin portal.',
                  'They should navigate to the Payments page and complete the subscription.',
                  'Once active, all students at your school will receive premium access.',
                ].map((item, index) => (
                  <Box key={index} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircle color="primary" sx={{ mr: 1 }} />
                    <Typography>{item}</Typography>
                  </Box>
                ))}
              </Box>

              {!loading && accessCategories.length > 0 && (
                <Box sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: 'rgba(25,118,210,0.04)' }}>
                  <Typography variant="h6" gutterBottom>Required individual payment amounts</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {accessCategories.map(item => (
                      <Chip
                        key={item.key}
                        label={`${item.label}: ₦${item.amount.toLocaleString('en-NG')}`}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
            <CardActions sx={{ p: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={() => navigate('/school/login')}
              >
                School Admin Login
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      <Box mt={6} textAlign="center">
        <Typography variant="body1" color="textSecondary">
          If you are a school admin and do not have access yet, contact support for help with onboarding.
        </Typography>
      </Box>
    </Container>
  );
};

export default Subscription;
